import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import classes from './header_tabs.module.scss';
import { ReactComponent as CallIcon } from '../../../../../assets/icons/call.svg';
import { ReactComponent as ClockIcon } from '../../../../../assets/icons/clock.svg';
import { ReactComponent as RightIcon } from '../../../../../assets/icons/arrow-right.svg';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit_pipeline.svg';
import { ReactComponent as GoogleMeetIcon } from '../../../../../assets/icons/google_meet.svg';
import { RangeDatePicker } from '../../../../_common/Form/DatePickers/RangeDatePicker/RangeDatePicker';
import useEventCallback from '../../../../../utils/hooks/useEventCallback';
import InputField from '../../../../_common/Form/TextFields/InputField';
import TimePicker from '../../../../_common/Form/DatePickers/TimePicker/TimePicker';
import Editor from '../../../../_common/Editor/Editor';
import Button from '../../../../_common/Form/Buttons/Button';
import Checkbox from '../../../../_common/Form/Checkbox/Checkbox';
import CancelModal from '../../ToggleModal/CancelModal/CancelModal';
import AssigneeSelect from '../../PossiblePersonsComponent/AssigneeSelect';

import {
  activityMarkAsDone,
  addActivityRequest,
  getActivityRequest,
  openActivityModal, resetActivityData, updateActivityData,
} from '../../../../../store/actions/possiblePersons';
import Loader from '../../../../_common/Loader/Loader';
import utils from '../../../../../utils/utils';

const timeBlockHeight = 84;

const currentHour = moment()
  .format('H');

const currentMinutes = moment()
  .format('mm');

const currentTimeTop = +currentHour * timeBlockHeight + +currentMinutes * 1.4;

const generateTime = (top, height) => {
  const [startHour, startM = 0] = `${top / 21 / 4}`.split('.');
  const [endHour, endM = 0] = `${((top / 21) / 4) + ((height / 21) / 4)}`.split('.');

  const startMinutes = 60 * `0.${startM}`;
  const endMinutes = 60 * `0.${endM}`;

  return {
    startTime: moment(`${startHour}:${startMinutes}`, 'h:mm')
      .format('hh:mm A'),
    endTime: moment(`${endHour}:${endMinutes}`, 'h:mm')
      .format('hh:mm A') !== 'Invalid date'
      ? moment(`${endHour}:${endMinutes}`, 'h:mm')
        .format('hh:mm A')
      : null,
  };
};
const generateSize = ({
  startTime,
  endTime,
}) => {
  const startHour = moment(startTime, 'hh:mm A')
    .format('H');
  const startMinutes = moment(startTime, 'hh:mm A')
    .format('mm');

  const endHour = moment(endTime, 'hh:mm A')
    .format('H');
  const endMinutes = moment(endTime, 'hh:mm A')
    .format('mm');

  const top = +startHour * timeBlockHeight + +startMinutes * 1.4;
  const bottom = +endHour * timeBlockHeight + +endMinutes * 1.4;
  const height = endTime ? bottom - top : 21;

  return {
    top,
    height,
    bottom: top + height,
  };
};

const Activity = ({ modal, className }) => {
  const { personsId } = useParams();
  const dispatch = useDispatch();
  const {
    singlePerson,
    activityData,
    openActivity,
    modalActivityData,
  } = useSelector((store) => store.possiblePersons);
  const scrollTimeout = useRef();
  const timeWrapperRef = useRef();
  const active = useRef(false);
  const resizeActive = useRef(false);
  const [timeHeight, setTimeHeight] = useState(21);
  const [assigneeId, setAssigneeId] = useState('');
  const [timeTop, setTimeTop] = useState();
  const [timeRange, setTimeRange] = useState({
    startTime: '',
    endTime: '',
  });
  const [openCancelModal, setOpenCancelModal] = useState(false);
  const [timeError, setTimeError] = useState({
    startTime: '',
    endTime: '',
    attendees: '',
    summary: '',
  });
  const [requestData, setRequestData] = useState({
    PotentialPatientId: personsId,
    note: '',
    summary: '',
    attendees: [singlePerson.email],
    type: 'Call',
  });
  const [date, setDate] = useState(null);

  const [saveLoad, setSaveLoad] = useState(false);
  const [activityLoad, setActivityLoad] = useState(true);

  const {
    note,
    summary,
    attendees,
    type,
  } = requestData;

  const activityCls = [classes.activity_wrapper, modal ? className : classes.media];

  const moveStop = useEventCallback(() => {
    if (active.current) {
      const step = Math.round(timeTop / 21) * 21;

      const {
        startTime,
        endTime,
      } = generateTime(step, timeHeight);

      setTimeRange({
        startTime,
        endTime,
      });

      setTimeTop(step);

      active.current = false;
    }

    if (resizeActive.current) {
      const step = Math.round(timeHeight / 21) * 21;
      const { endTime } = generateTime(timeTop, step);
      setTimeRange((prev) => ({
        ...prev,
        endTime,
      }));

      setTimeHeight(step);

      setTimeError((prev) => ({
        ...prev,
        endTime: '',
      }));
      resizeActive.current = false;
    }
  }, [timeTop, timeHeight]);

  useEffect(() => {
    window.addEventListener('mouseup', moveStop, true);

    return () => {
      window.removeEventListener('mouseup', moveStop, true);
      dispatch(resetActivityData(modal ? 'modalActivityData' : 'activityData'));
    };
  }, []);

  useEffect(() => {
    if (date) {
      (async () => {
        setActivityLoad(true);
        await dispatch(getActivityRequest({
          id: singlePerson.id,
          start: moment(date)
            .utc()
            .format('YYYY-MM-DD HH:mm'),
          end: moment(date)
            .add(1, 'day')
            .utc()
            .format('YYYY-MM-DD HH:mm'),
        }, modal ? 'modalActivityData' : 'activityData'));

        setActivityLoad(false);
      })();
    }
  }, [date]);

  useEffect(() => {
    setDate(modal
      ? moment(openActivity.start)
        .local()
        .format('YYYY-MM-DD')
      : moment()
        .format('YYYY-MM-DD'));

    if (modal) {
      setRequestData({
        PotentialPatientId: openActivity.PotentialPatientId,
        note: openActivity.note,
        summary: openActivity.summary,
        type: openActivity.type,
      });
      const modalTimeRange = {
        startTime: moment(openActivity.start)
          .format('hh:mm A'),
        endTime: moment(openActivity.end)
          .format('hh:mm A'),
      };

      setTimeRange(modalTimeRange);
      const {
        top,
        height,
      } = generateSize(modalTimeRange);

      timeWrapperRef.current.scrollTo(0, top);

      setTimeTop(top);
      setTimeHeight(height);
    }
  }, []);

  const setTime = (hour, minutes, startTime) => {
    const blockTop = hour * timeBlockHeight + minutes * 1.4;

    const { endTime } = generateTime(blockTop, timeHeight);

    if (!endTime) {
      const blockBottom = 24 * timeBlockHeight - blockTop;
      setTimeHeight(blockBottom);
    }

    setTimeTop(blockTop);
    setTimeRange({
      startTime,
      endTime: endTime || '12:00 AM',
    });

    setTimeError((prev) => ({
      ...prev,
      startTime: '',
      endTime: '',
    }));
  };
  const timeRangeSize = (e) => {
    if (resizeActive.current) {
      setTimeHeight((prev) => Math.min(Math.max(prev + e.movementY, 21), 2016 - timeHeight));
    }
  };

  const moveTimeBlock = useCallback((ev) => {
    if (active.current) {
      setTimeTop((p) => {
        let pos = Math.max(p + ev.movementY, 0);
        pos = Math.min(pos, 2016 - timeHeight);

        const moveStep = 2;
        if (pos + timeHeight >= timeWrapperRef.current.clientHeight + timeWrapperRef.current.scrollTop) {
          timeWrapperRef.current.scrollBy({ top: moveStep });
          clearTimeout(scrollTimeout.current);
          scrollTimeout.current = setTimeout(() => {
            moveTimeBlock({ movementY: moveStep });
          }, 10);
        } else if (pos <= timeWrapperRef.current.scrollTop) {
          timeWrapperRef.current.scrollBy({ top: moveStep * -1 });
          clearTimeout(scrollTimeout.current);
          scrollTimeout.current = setTimeout(() => {
            moveTimeBlock({ movementY: moveStep * -1 });
          }, 10);
        }

        return pos;
      });
    }
  }, [active.current, timeHeight]);

  const changeTime = useCallback((time, path) => {
    if ((path === 'startTime' && (moment(time, 'hh:mm A')
      .isBefore(moment(timeRange.endTime, 'hh:mm A'))))
      || (path === 'startTime' && !timeRange.endTime)
      || (path === 'endTime' && (time.length > 1) && (time.length < 8))
      || ((moment(timeRange.startTime, 'hh:mm A')
        .isBefore(moment(time, 'hh:mm A'))
        && path === 'endTime'))
      || time === ''
    ) {
      setTimeRange((prev) => {
        let range = {};

        if (path === 'startTime' && !time) {
          range = {
            ...prev,
            [path]: time,
            endTime: '',
          };
        } else {
          range = {
            ...prev,
            [path]: time,
          };
        }

        if (time.length === 8) {
          const {
            top,
            height,
          } = generateSize(range);

          setTimeTop(top);
          setTimeHeight(height);
        } else {
          setTimeTop('');
        }

        return range;
      });

      setTimeError((prev) => ({
        ...prev,
        [path]: '',
      }));
    } else if (timeRange.startTime) {
      setTimeError((prev) => ({
        ...prev,
        endTime: 'Must be greater',
      }));

      setTimeRange((prev) => ({ ...prev, endTime: '' }));

      setTimeTop('');
    } else {
      setTimeError((prev) => ({
        ...prev,
        startTime: 'Start time is required',
      }));
      setTimeTop('');
    }
  }, [timeRange]);

  const changeActivityData = (value, path) => {
    setTimeError((prev) => ({ ...prev, [path]: '' }));

    if (path === 'attendees') {
      setAssigneeId(value.id);
      setRequestData((prev) => ({
        ...prev,
        [path]: [...prev[path], value.email],
      }));
    } else {
      setRequestData((prev) => ({
        ...prev,
        [path]: value,
      }));
    }
  };

  const cancelClick = () => {
    if (modal) {
      dispatch(openActivityModal(false));
    } else if (note || summary || attendees.length > 1
      || timeRange.startTime || timeRange.endTime) {
      setOpenCancelModal(true);
    }
  };
  const clearClick = () => {
    setRequestData({
      PotentialPatientId: personsId,
      note: '',
      summary: '',
      attendees: [singlePerson.email],
      type: 'Call',
    });
    setTimeRange({
      startTime: '',
      endTime: '',
    });
    setAssigneeId('');

    setTimeHeight(21);
    setTimeTop(null);
    setOpenCancelModal(false);
  };

  const activitySaveClick = async () => {
    setSaveLoad(true);

    let hasError = false;

    const requiredFields = ['startTime', 'endTime'];

    _.forEach(timeRange, (val, key) => {
      if ((!val) && requiredFields.includes(key)) {
        setTimeError((prev) => ({ ...prev, [key]: 'Field is required' }));

        hasError = true;
      }
    });

    if (!modal && requestData.attendees.length < 2) {
      setTimeError((prev) => ({ ...prev, attendees: 'Field is required' }));

      hasError = true;
    }

    if (!requestData.summary) {
      setTimeError((prev) => ({ ...prev, summary: 'Field is required' }));

      hasError = true;
    }

    if (hasError) {
      toast.error('Please fill this fields');
    } else {
      const requestField = {
        ...requestData,
        start: new Date(`${date} ${timeRange.startTime}`).toISOString(),
        end: new Date(`${date} ${timeRange.endTime}`).toISOString(),
      };

      if (modal) {
        delete requestField.attendees;

        const { payload: { status } } = await dispatch(updateActivityData({
          ...requestField,
          id: openActivity.id,
        }));

        if (status === 'ok') {
          toast.success('Successful update activity');
          dispatch(openActivityModal({}));
        }
      } else {
        const {
          payload: { data: { data: { status } } },
        } = await dispatch(addActivityRequest(requestField, 'activityData'));

        if (status === 'ok') {
          toast.success(`${requestField.type} is created`);
          clearClick();
        }
      }
    }

    setSaveLoad(false);
  };

  const calendarData = useMemo(
    () => (modal ? modalActivityData : activityData),
    [modal, modalActivityData, activityData],
  );

  const markAsDoneRequest = async (checked) => {
    await dispatch(activityMarkAsDone({ id: openActivity.id, status: +checked }));
  };

  return (
    <div
      className={activityCls.join(' ')}
      onMouseMove={(e) => moveTimeBlock(e)}
    >
      {modal && <h2>Edit activity</h2>}

      <div className={classes.activity_icon_buttons_wrapper}>

        <div className={classes.activity_icon_buttons}>
          <div
            role="button"
            tabIndex="0"
            onClick={() => changeActivityData('Call', 'type')}
            className={`${classes.activity_icon} ${type === 'Call' ? classes.active : ''}`}
          >
            <CallIcon />
          </div>

          <div
            role="button"
            tabIndex="0"
            onClick={() => changeActivityData('Task', 'type')}
            className={`${classes.activity_icon} ${type === 'Task' ? classes.active : ''}`}
          >
            <ClockIcon />
          </div>

        </div>

        {/* {modal && ( */}
        {/*   <a */}
        {/*     href={openActivity.meetUrl} */}
        {/*     target="_blank" */}
        {/*     className={classes.google_meet_button} */}
        {/*     rel="noreferrer" */}
        {/*   > */}
        {/*     <GoogleMeetIcon /> */}
        {/*   </a> */}
        {/* )} */}
      </div>

      <div className={classes.activity_description_wrapper}>
        <div className={classes.activity_description_left}>
          <InputField
            onChange={(value) => changeActivityData(value, 'summary')}
            label="Activity name"
            value={summary}
            wrapperClassName={classes.activity_description_left_item}
            error={timeError.summary}
          />

          {!modal
            && (
            <AssigneeSelect
              error={timeError.attendees}
              assigneeId={assigneeId}
              onchange={(item) => changeActivityData(item, 'attendees')}
            />
            )}

          <div className={classes.activity_description_left_time_block}>
            <TimePicker
              onChange={(time) => changeTime(time, 'startTime')}
              time={timeRange.startTime}
              label="Start time"
              wrapperClassName={classes.activity_description_left_time_block_item}
              dateFormat="hh:mm A"
              error={timeError.startTime}
            />

            <TimePicker
              onChange={(time) => changeTime(time, 'endTime')}
              time={timeRange.endTime}
              label="End time"
              dateFormat="hh:mm A"
              wrapperClassName={classes.activity_description_left_time_block_item}
              error={timeError.endTime}
            />
          </div>
        </div>

        <div className={classes.activity_description_right}>
          <Editor value={note} setEditorValue={(value) => changeActivityData(value, 'note')} />
        </div>
      </div>

      <div className={classes.activity_date}>
        <div className={classes.activity_calendar}>
          <RangeDatePicker
            currentDate={
              { start: date }
            }
            onChangeDate={(day) => setDate(day)}
            show
            showMonthsCount={1}
            selectsRange
          />
        </div>

        <div
          ref={timeWrapperRef}
          className={classes.activity_calendar_time}
        >
          {activityLoad && <Loader />}

          {_.range(24)
            .map((h) => (
              <div key={h} className={classes.activity_calendar_time_row}>
                <div className={classes.activity_calendar_time_item}>
                  {moment()
                    .startOf('day')
                    .add(h, 'hour')
                    .format('hh:mm A')}
                </div>

                <div
                  className={classes.activity_calendar_time_body}
                >
                  {[0, 15, 30, 45].map((m) => {
                    const fullTime = moment()
                      .startOf('day')
                      .add(h, 'hour')
                      .add(m, 'minute')
                      .format('hh:mm A');

                    return (
                      <div
                        role="button"
                        tabIndex="0"
                        aria-label="Mute volume"
                        key={m}
                        onClick={() => setTime(h, m, fullTime)}
                        onMouseMove={(e) => timeRangeSize(e)}
                      />
                    );
                  })}
                </div>
              </div>
            ))}

          <div
            style={{ top: currentTimeTop }}
            className={classes.current_time_row}
          >
            <div className={classes.current_time_text}>
              {moment()
                .format('hh:mm A')}
            </div>
            <div className={classes.current_time} />
          </div>

          <div
            role="button"
            tabIndex="0"
            style={{
              height: timeHeight,
              top: typeof timeTop === 'number' ? timeTop : 'unset',
              display: typeof timeTop === 'number' ? 'block' : 'none',
            }}
            className={classes.selected_time}
            onMouseMove={(e) => timeRangeSize(e)}
          >
            <div
              role="button"
              tabIndex="0"
              onMouseDown={() => {
                active.current = true;
              }}
            >
              <div className={classes.selected_time_text_header}>
                {type === 'Call' ? <CallIcon /> : <ClockIcon />}

                <p>{type}</p>
              </div>

              {timeHeight > 21 && (
                <div className={classes.selected_time_text}>
                  <p>{timeRange.startTime}</p>

                  <RightIcon />

                  <p>{timeRange.endTime}</p>
                </div>
              )}

            </div>

            <div
              role="button"
              tabIndex="0"
              aria-label="Mute volume"
              className={classes.resize_block}
              onMouseDown={() => {
                resizeActive.current = true;
              }}
            />
          </div>

          {calendarData.filter((a) => a.id !== openActivity.id)
            .map((a) => {
              const {
                top,
                height,
                bottom,
              } = generateSize({
                endTime: moment(a.end).format('hh:mm A'),
                startTime: moment(a.start).format('hh:mm A'),
              });
              const groupData = _.chain(calendarData)
                .filter((d) => {
                  const size = generateSize({
                    endTime: moment(d.end).format('hh:mm A'),
                    startTime: moment(d.start).format('hh:mm A'),
                  });
                  return (top <= size.top && bottom > size.top)
                    || (top >= size.top && bottom < size.bottom)
                    || (top >= size.top && top < size.bottom);
                })
                .uniqWith((d1, d2) => {
                  const size1 = generateSize({
                    endTime: moment(d1.end).format('hh:mm A'),
                    startTime: moment(d1.start).format('hh:mm A'),
                  });
                  const size2 = generateSize({
                    endTime: moment(d2.end).format('hh:mm A'),
                    startTime: moment(d2.start).format('hh:mm A'),
                  });
                  const isOver = (size2.top <= size1.top && size2.bottom > size1.top)
                    || (size2.top >= size1.top && size2.bottom < size1.bottom)
                    || (size2.top >= size1.top && size2.top < size1.bottom);

                  return !isOver;
                })
                .value();
              const index = groupData.findIndex((d) => d.id === a.id);
              const left = `calc(${(100 / groupData.length) * index}% + ${75 - ((75 / groupData.length) * index)}px)`;
              const width = `calc(${100 / groupData.length}% - ${75 / groupData.length}px)`;

              return (
                <React.Fragment key={a.id}>
                  {!modal && (
                    <div
                      role="button"
                      tabIndex="0"
                      onClick={() => dispatch(openActivityModal(a))}
                      className={classes.edit_calendar_time}
                      style={{
                        top,
                        left: `calc(${(100 / groupData.length) * index}% 
                      + ${75 - ((75 / groupData.length) * index)}px 
                      + ${100 / groupData.length}% 
                      - ${75 / groupData.length}px - 30px)`,
                      }}
                    >
                      <EditIcon />
                    </div>
                  )}

                  <div
                    className={classes.selected_time_finished}
                    style={{
                      height,
                      top,
                      left,
                      width,
                    }}
                  >
                    <div className={classes.selected_time_text_header}>
                      {a.type === 'Call' ? <CallIcon /> : <ClockIcon />}

                      <p>{a.type}</p>
                    </div>

                    {generateSize({
                      endTime: moment(a.end)
                        .format('hh:mm A'),
                      startTime: moment(a.start)
                        .format('hh:mm A'),
                    }).height > 21 && (
                      <div className={classes.selected_time_text}>
                        <p>
                          {moment(a.start)
                            .format('hh:mm A')}
                        </p>

                        <RightIcon />

                        <p>
                          {moment(a.end)
                            .format('hh:mm A')}
                        </p>
                      </div>
                    )}
                  </div>

                </React.Fragment>
              );
            })}
        </div>
      </div>

      <div className={classes.activity_buttons}>
        <Button
          onClick={activitySaveClick}
          type="button"
          title="Save"
          reverseColor
          loading={saveLoad}
          className={classes.button}
          disabled={modal && !!openActivity.status}
        />

        <Button
          onClick={cancelClick}
          type="button"
          title="Cancel"
          clearButton
        />

        {modal && (
          <Checkbox
            className={classes.activity_buttons_checkbox}
            onChange={(check) => markAsDoneRequest(check)}
            label="Mark as done"
            checked={!!openActivity.status}
          />
        )}
      </div>

      <CancelModal
        activity
        close={() => setOpenCancelModal(false)}
        open={openCancelModal}
        clearClick={clearClick}
      />

    </div>
  );
};

export default Activity;
